/**
 * UI copy + helpers for flights_new LDS proposal / BOOK status (backend: lds_reservation_status).
 */

import { isFlightsNewProposalPackage } from "./dynamicPackageDisplayMode";

/** Review payment card — bank transfer + send PROPOSAL to LDS (same action, not a third CTA) */
export const FLIGHTS_PROPOSAL_PRIMARY_CTA_HE =
  "תשלום בהעברה בנקאית";

/** Review body — credit card opens Yad Sarig before any LDS proposal submit */
export const FLIGHTS_CREDIT_CARD_SARIG_CTA_HE = "תשלום בכרטיס אשראי";

/**
 * Flight proposal summary page: show success UI only when transfer to supplier is reflected.
 * Aligns with GET /api/flight-proposals normalized fields + hr_flight_module slice.
 */
export function flightProposalTransferredToSupplier(proposal) {
  if (!proposal || typeof proposal !== "object") return false;
  if (proposal.proposal_sent === true) return true;
  if (proposal.order_response_exists === true) return true;
  const st = proposal.hr_flight_module?.lds_reservation_status;
  if (st === "proposal_pushed_to_lds" || st === "booked_in_lds") return true;
  const last = String(proposal.last_reservation_type_sent || "").toUpperCase();
  if (last === "PROPOSAL" || last === "BOOK") return true;
  return false;
}

export const FLIGHTS_LDS_STATUS_LABELS_HE = {
  proposal_pushed_to_lds:
    "הבקשה נשלחה לספק — ממתין לאישור סופי (טיוטת ספק / PROPOSAL)",
  lds_reservation_rejected: "שגיאה ביצירת הצעה",
  lds_reservation_ambiguous: "סטטוס לא ברור מול הספק",
  booked_in_lds: "אישור סופי מול הספק (BOOK)",
};

export function heLabelForLdsReservationStatus(status) {
  if (status == null || status === "") {
    return "טרם נשלחה הצעה לספק";
  }
  return FLIGHTS_LDS_STATUS_LABELS_HE[status] || String(status);
}

/** Show retry when proposal flow and LDS did not succeed or response missing */
export function flightsLdsRetryEligible(dynamicRow) {
  if (!dynamicRow || !isFlightsNewProposalPackage(dynamicRow)) return false;
  if (!dynamicRow.ldsorderid) return false;
  const st = dynamicRow.lds_reservation_status;
  if (!dynamicRow.flights?.order_response) return true;
  return st === "lds_reservation_rejected" || st === "lds_reservation_ambiguous";
}

/** After a successful proposal push, avoid implying the user must submit again */
export function flightsLdsProposalAlreadySent(dynamicRow) {
  if (!dynamicRow || !isFlightsNewProposalPackage(dynamicRow)) return false;
  const st = dynamicRow.lds_reservation_status;
  return st === "proposal_pushed_to_lds" || st === "booked_in_lds";
}

/**
 * Best-effort PNR / supplier refs from flights.order_response (shape varies by supplier).
 * @returns {{ supplierPNR: string|null, recordLocator: string|null, summary: string|null }}
 */
export function extractFlightsOrderResponseRefs(dynamicRow) {
  const raw = dynamicRow?.flights?.order_response;
  if (!raw || typeof raw !== "object") {
    return { supplierPNR: null, recordLocator: null, summary: null };
  }
  const res = raw.reservation && typeof raw.reservation === "object" ? raw.reservation : raw;
  const supplierPNR =
    res.supplierPNR ??
    res.supplier_pnr ??
    res.SupplierPNR ??
    raw.supplierPNR ??
    null;
  const recordLocator =
    res.recordLocator ??
    res.pnr ??
    res.PNR ??
    raw.recordLocator ??
    raw.sabrePnr ??
    null;
  const op = raw.opStatus || res.opStatus;
  const opMsg =
    op && typeof op === "object"
      ? [op.status, op.message, op.description].filter(Boolean).join(" — ")
      : "";
  const summary =
    opMsg ||
    (typeof raw.message === "string" ? raw.message : null) ||
    (typeof raw.error === "string" ? raw.error : null) ||
    null;
  return {
    supplierPNR: supplierPNR != null && supplierPNR !== "" ? String(supplierPNR) : null,
    recordLocator: recordLocator != null && recordLocator !== "" ? String(recordLocator) : null,
    summary: summary ? String(summary) : null,
  };
}
